import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Mail, MapPin, Phone, Instagram, Twitter, Facebook } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import BrandLogo from "./BrandLogo";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const { pathname } = useLocation(); // eslint-disable-line no-unused-vars

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/newsletter`, { email, source: "footer" });
      toast.success(data.message || "Subscribed!");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-welljio-ink text-white mt-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-10 lg:gap-12">
          <div className="col-span-2 md:col-span-2 lg:col-span-2 space-y-6">
            <Link to="/" className="inline-flex items-center" aria-label="Welljio home">
              <BrandLogo className="h-10 w-auto" variant="light" />
            </Link>
            <p className="text-slate-300 max-w-sm leading-relaxed font-body">
              Premium physiotherapy-led wellness products engineered for posture correction, pain relief and everyday ergonomic comfort.
            </p>
            <form onSubmit={subscribe} className="flex gap-2 max-w-md" data-testid="footer-newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for wellness tips"
                className="flex-1 bg-white/10 border border-white/15 rounded-full px-5 py-3 text-sm placeholder:text-slate-400 outline-none focus:border-welljio-teal"
              />
              <button
                data-testid="newsletter-submit-button"
                disabled={loading}
                className="bg-welljio-teal text-welljio-ink font-semibold rounded-full px-6 text-sm hover:bg-white transition-colors"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-5">Shop</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/shop?category=neck-cervical" className="text-slate-300 hover:text-white transition-colors">Neck & Cervical</Link></li>
              <li><Link to="/shop?category=back-posture" className="text-slate-300 hover:text-white transition-colors">Back & Posture</Link></li>
              <li><Link to="/shop?category=orthopedic-cushions" className="text-slate-300 hover:text-white transition-colors">Orthopedic Cushions</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-5">Company</p>
            <ul className="space-y-3 text-sm">
              <li><Link to="/about" className="text-slate-300 hover:text-white transition-colors">About</Link></li>
              <li><Link to="/blog" className="text-slate-300 hover:text-white transition-colors">Blog</Link></li>
              <li><Link to="/contact" className="text-slate-300 hover:text-white transition-colors">Contact</Link></li>
              <li><Link to="/account" className="text-slate-300 hover:text-white transition-colors">My Account</Link></li>
              <li><Link to="/faq" className="text-slate-300 hover:text-white transition-colors">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-widest text-slate-400 mb-5">Support</p>
            <ul className="space-y-3 text-sm">
              <li className="flex items-center gap-2 text-slate-300"><Mail className="w-4 h-4" strokeWidth={1.5} /> support@welljio.com</li>
              <li className="flex items-center gap-2 text-slate-300"><Phone className="w-4 h-4" strokeWidth={1.5} /> +91 80 4567 8910</li>
              <li className="flex items-center gap-2 text-slate-300"><MapPin className="w-4 h-4" strokeWidth={1.5} /> Hyderabad, India</li>
            </ul>
            <div className="flex items-center gap-3 mt-6">
              <a href="#" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><Instagram className="w-4 h-4" strokeWidth={1.5} /></a>
              <a href="#" aria-label="Twitter" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><Twitter className="w-4 h-4" strokeWidth={1.5} /></a>
              <a href="#" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"><Facebook className="w-4 h-4" strokeWidth={1.5} /></a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-slate-400">© 2026 Anaya Medical Systems Pvt. Ltd. All rights reserved.</p>
          <div className="flex items-center gap-6 text-xs text-slate-400">
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <span>GST: 29ABCDE1234F1Z5</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
